import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Dialog } from 'primereact/dialog';
import Layout from '../component/layout'
import { useNavigate } from 'react-router-dom';
import { toastify } from '../../../utils/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getUnitsForFacility } from '../../../utils/urls';
import { getItem } from '../../../utils/localStorage'
import { AddNewCustomerURL, getCustomersURL, ImportCustomersURL } from '../../../utils/urls';

// Kenyan KRA PIN format: 1 letter + 9 digits + 1 letter, e.g. A001234567Z
const KRA_PIN_REGEX = /^[A-Za-z]\d{9}[A-Za-z]$/;

function AddCustomer() {
    const navigate = useNavigate()
    const toast = useRef(null);
    const [formErrors, setFormErrors] = useState({});
    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const emailRef = useRef();
    const phoneNumberRef = useRef();
    const idNumberRef = useRef();
    const kraPinRef = useRef();
    const customerTypeRef = useRef();
    const residentTypeRef = useRef(null);
    const unitsRef = useRef(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [idNumber, setIdNumber] = useState('');
    const [nextOfKinName, setNextOfKinName] = useState('');
    const [nextOfKinRelationship, setNextOfKinRelationship] = useState('');
    const [nextOfKinPhoneNumber, setNextOfKinPhoneNumber] = useState('');
    const [nextOfKinEmail, setNextOfKinEmail] = useState('');
    const [canReceiveInvoice, setCanReceiveInvoice] = useState(false);
    const [combineInvoices, setCombineInvoices] = useState(false);
    const [kraPin, setKraPin] = useState('');
    const [units, setUnits] = useState([]);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [customerType, setCustomerType] = useState('');
    const [residentType, setResidentType] = useState('');
    const [nonResidentUsage, setNonResidentUsage] = useState('');
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importResults, setImportResults] = useState(null);
    const [importCompleted, setImportCompleted] = useState(false);
    const [importProgress, setImportProgress] = useState({
        phase: '',
        message: '',
        total: 0
    });
    const [customerKind, setCustomerKind] = useState("Individual");

    const [salutation, setSalutation] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [companyName, setCompanyName] = useState("");

    const customerKindRef = useRef(null);
    const displayNameRef = useRef(null);
    const companyNameRef = useRef(null);

    const fetchUnits = async (customerType) => {
        try {
            const facilityId = await getItem('selectedFacilityId');

            const unitResponse = await makeRequest2(`${getUnitsForFacility}/${facilityId}`, 'GET', {});
            if (!unitResponse.success) throw new Error('Error fetching units');

            const customerResponse = await makeRequest2(`${getCustomersURL}/${facilityId}`, 'GET', {});

            if (!customerResponse.success) throw new Error('Error fetching customers');

            // Create a map of homeownerId -> residentType
            const homeownerMap = {};
            customerResponse.data.forEach(customer => {
                if (customer.customerType === "home owner") {
                    homeownerMap[customer._id] = customer.residentType;
                }
            });

            let filteredUnits = [];

            if (customerType === "home owner") {
                // Show unoccupied units OR units that have a tenant but NO homeowner
                filteredUnits = unitResponse.data.filter(unit => {
                    return (!unit.homeOwnerId && !unit.tenantId) || (unit.tenantId && !unit.homeOwnerId);
                });
            } else if (customerType === "tenant") {
                // Show units where homeowner is non-resident and there is no tenant yet
                filteredUnits = unitResponse.data.filter(unit => {
                    return unit.homeOwnerId && homeownerMap[unit.homeOwnerId] === "non-resident" && !unit.tenantId;
                });
            } else {
                // Default: Only show completely unoccupied units
                filteredUnits = unitResponse.data.filter(unit => !unit.homeOwnerId && !unit.tenantId);
            }


            setUnits(filteredUnits);
        } catch (err) {
            console.error('Error fetching units:', err);
        }
    };

    const HandleCustomerSubmit = async () => {
        try {
            setFormErrors({});

            const facilityId = await getItem('selectedFacilityId');

            const trimmedKraPin = kraPin
                .trim()
                .toUpperCase();

            const errors = {};


            // =====================================================
            // CUSTOMER KIND
            // =====================================================

            if (!customerKind) {
                errors.customerKind =
                    'Customer type is required.';
            }


            // =====================================================
            // BUSINESS VALIDATION
            // =====================================================

            if (
                customerKind === "Business" &&
                !companyName.trim()
            ) {
                errors.companyName =
                    'Company name is required.';
            }


            // =====================================================
            // COMMON PERSONAL / CONTACT DETAILS
            // These are required for BOTH Individual and Business.
            // For Business they represent the primary contact.
            // =====================================================

            if (!firstName.trim()) {
                errors.firstName =
                    'First name is required.';
            }

            if (!lastName.trim()) {
                errors.lastName =
                    'Last name is required.';
            }

            if (!phoneNumber.trim()) {
                errors.phoneNumber =
                    'Phone number is required.';
            }

            if (!idNumber.trim()) {
                errors.idNumber =
                    'ID number is required.';
            }


            // =====================================================
            // DISPLAY NAME
            // =====================================================

            if (!displayName.trim()) {
                errors.displayName =
                    'Display name is required.';
            }


            // =====================================================
            // KRA PIN
            // =====================================================

            if (
                trimmedKraPin &&
                !KRA_PIN_REGEX.test(trimmedKraPin)
            ) {
                errors.kraPin =
                    'Invalid KRA PIN. Expected format: one letter, 9 digits, one letter (e.g. A001234567Z).';
            }


            // =====================================================
            // UNITS
            // =====================================================

            if (
                !selectedUnits ||
                selectedUnits.length === 0
            ) {
                errors.units =
                    'Please select at least one unit.';
            }


            // =====================================================
            // EXISTING CUSTOMER TYPE
            // Home Owner / Tenant
            // =====================================================

            if (!customerType) {
                errors.customerType =
                    'Customer type is required.';
            }


            // =====================================================
            // RESIDENT TYPE
            // =====================================================

            if (!residentType) {
                errors.residentType =
                    'Resident type is required.';
            }


            // =====================================================
            // HANDLE VALIDATION ERRORS
            // =====================================================

            if (Object.keys(errors).length > 0) {

                setFormErrors(errors);

                const fieldOrder = [
                    {
                        key: 'customerKind',
                        ref: customerKindRef
                    },

                    {
                        key: 'companyName',
                        ref: companyNameRef
                    },

                    {
                        key: 'salutation',
                        ref: null
                    },

                    {
                        key: 'firstName',
                        ref: firstNameRef
                    },

                    {
                        key: 'lastName',
                        ref: lastNameRef
                    },

                    {
                        key: 'displayName',
                        ref: displayNameRef
                    },

                    {
                        key: 'email',
                        ref: emailRef
                    },

                    {
                        key: 'phoneNumber',
                        ref: phoneNumberRef
                    },

                    {
                        key: 'idNumber',
                        ref: idNumberRef
                    },

                    {
                        key: 'kraPin',
                        ref: kraPinRef
                    },

                    {
                        key: 'customerType',
                        ref: customerTypeRef
                    },

                    {
                        key: 'residentType',
                        ref: residentTypeRef
                    },

                    {
                        key: 'units',
                        ref: unitsRef
                    }
                ];


                const firstErrorField =
                    fieldOrder.find(
                        ({ key }) => errors[key]
                    );


                if (
                    firstErrorField?.ref?.current
                ) {

                    firstErrorField.ref.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });


                    if (
                        typeof firstErrorField.ref.current.focus ===
                        'function'
                    ) {
                        firstErrorField.ref.current.focus();
                    }
                }

                return;
            }


            // =====================================================
            // CUSTOMER DATA
            // =====================================================

            const customerData = {

                // NEW
                customerKind,

                // Business only
                ...(customerKind === "Business" && {
                    companyName: companyName.trim()
                }),

                // New fields
                salutation,
                displayName: displayName.trim(),


                // =================================================
                // EXISTING FIELDS - DON'T CHANGE
                // =================================================

                firstName: firstName.trim(),
                lastName: lastName.trim(),

                email: email.trim(),
                phoneNumber: phoneNumber.trim(),
                idNumber: idNumber.trim(),

                nextOfKinName,
                nextOfKinRelationship,
                nextOfKinPhoneNumber,
                nextOfKinEmail,

                canReceiveInvoice,
                combineInvoices,

                kraPin: trimmedKraPin,

                units: selectedUnits,

                customerType,

                residentType,

                nonResidentUsage
            };


            console.log(
                "Customer Data:",
                customerData
            );


            // =====================================================
            // SUBMIT
            // =====================================================

            const response = await makeRequest2(
                `${AddNewCustomerURL}/${facilityId}`,
                'POST',
                customerData
            );


            // =====================================================
            // SUCCESS
            // =====================================================

            if (response.success) {

                toastify(
                    'Customer added successfully',
                    'success'
                );


                // =================================================
                // MOVE-IN HANDOVERS
                // =================================================

                if (
                    response.data &&
                    response.data.handovers &&
                    response.data.handovers.total > 0
                ) {

                    const {
                        successful,
                        failed
                    } = response.data.handovers;


                    if (successful > 0) {

                        toastify(
                            `Move-in handover${successful > 1 ? 's' : ''} automatically created for ${successful} unit${successful > 1 ? 's' : ''}`,
                            'success'
                        );
                    }


                    if (failed > 0) {

                        toastify(
                            `Warning: ${failed} move-in handover${failed > 1 ? 's' : ''} could not be created`,
                            'warning'
                        );
                    }
                }


                // =================================================
                // CLEAR AND NAVIGATE
                // =================================================

                handleClear();

                navigate(
                    '/facility/customer_management/customers'
                );

            } else {

                throw new Error(
                    response.error
                );
            }

        } catch (err) {

            console.log(err);

            toastify(
                err.message,
                'error'
            );
        }
    };

    const downloadCSVTemplate = () => {
        const headers = [
            "firstName",
            "lastName",
            "email",
            "phoneNumber",
            "idNumber",
            "kraPin",
            "customerType",
            "residentType",
            "units",
            "canReceiveInvoice", // Add to CSV template
            "combineInvoices" // Add to CSV template
        ];

        const csvContent = [headers.join(",")].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "customer_import_template.csv";
        link.click();
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.current.show({
                    severity: 'error',
                    summary: 'File too large',
                    detail: 'File size must be less than 5MB',
                    life: 5000
                });
                return;
            }

            if (!file.name.endsWith('.csv')) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Invalid file type',
                    detail: 'Please upload a CSV file',
                    life: 5000
                });
                return;
            }

            setSelectedFile(file);
            setImportResults(null);
        }
    };

    const parseCSV = (text) => {
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        const customers = [];

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const values = line.split(',').map(v => v.trim());
            const customer = {};

            headers.forEach((header, index) => {
                if (values[index] !== undefined) {
                    customer[header] = values[index];
                }
            });

            // Parse units if provided
            if (customer.units) {
                customer.units = customer.units.split(';').map(unit => ({
                    name: unit.trim()
                }));
            }

            // Convert canReceiveInvoice to boolean
            if (customer.canReceiveInvoice) {
                customer.canReceiveInvoice = customer.canReceiveInvoice.toLowerCase() === 'true';
            }

            // Convert combineInvoices to boolean
            if (customer.combineInvoices) {
                customer.combineInvoices = customer.combineInvoices.toLowerCase() === 'true';
            }

            // Normalize KRA PIN if provided
            if (customer.kraPin) {
                customer.kraPin = customer.kraPin.trim().toUpperCase();
            }

            customers.push(customer);
        }

        return customers;
    };

    const handleImportSubmit = async () => {
        if (!selectedFile) {
            toast.current.show({
                severity: 'warn',
                summary: 'No file selected',
                detail: 'Please select a CSV file to upload',
                life: 3000
            });
            return;
        }

        setUploading(true);
        setImportCompleted(false);
        setImportResults(null);
        setUploadProgress(0);
        setImportProgress({
            phase: 'validating',
            message: 'Validating CSV file...',
            total: 0
        });

        try {
            const reader = new FileReader();

            reader.onload = async (event) => {
                try {
                    const text = event.target.result;
                    const customers = parseCSV(text);

                    if (customers.length === 0) {
                        throw new Error('No valid customer data found in CSV');
                    }

                    // Update progress
                    setUploadProgress(20);
                    setImportProgress({
                        phase: 'validating',
                        message: `Validating ${customers.length} customers...`,
                        total: customers.length
                    });

                    // Validate customers
                    const validationErrors = [];
                    const validatedCustomers = customers.map((customer, index) => {
                        const rowErrors = [];

                        // Required fields
                        if (!customer.firstName) rowErrors.push('First name required');
                        if (!customer.lastName) rowErrors.push('Last name required');
                        if (!customer.phoneNumber) rowErrors.push('Phone number required');

                        // KRA PIN is optional, but if provided it must be valid
                        if (customer.kraPin && !KRA_PIN_REGEX.test(customer.kraPin)) {
                            rowErrors.push('Invalid KRA PIN format');
                        }

                        if (rowErrors.length > 0) {
                            validationErrors.push(`Row ${index + 2}: ${rowErrors.join(', ')}`);
                            return null;
                        }

                        return {
                            ...customer,
                            email: customer.email ? customer.email.toLowerCase().trim() : null,
                            phoneNumber: customer.phoneNumber.toString().slice(-9)
                        };
                    }).filter(c => c !== null);

                    if (validationErrors.length > 0 && validatedCustomers.length === 0) {
                        throw new Error(`All rows have validation errors:\n${validationErrors.slice(0, 5).join('\n')}`);
                    }

                    // Start import
                    setUploadProgress(50);
                    setImportProgress({
                        phase: 'uploading',
                        message: 'Sending data to server...',
                        total: validatedCustomers.length
                    });

                    const facilityId = await getItem('selectedFacilityId');

                    const response = await makeRequest2(
                        `${ImportCustomersURL}/${facilityId}`,
                        'POST',
                        { customers: validatedCustomers }
                    );

                    if (response.success) {

                        setUploadProgress(80);
                        setImportProgress({
                            phase: 'processing',
                            message: 'Processing import on server...',
                            total: validatedCustomers.length
                        });

                        // ✅ Store results - access from response.data
                        setImportResults({
                            summary: response.data.summary,
                            results: response.data.results
                        });

                        // ✅ Immediately mark as completed
                        setImportCompleted(true);
                        setUploading(false);

                        // Show final progress
                        setTimeout(() => {
                            setUploadProgress(100);
                            setImportProgress({
                                phase: 'completing',
                                message: 'Import completed successfully!',
                                total: validatedCustomers.length
                            });

                            // ✅ Safe access for toast message - from response.data
                            let successMessage = 'Import completed successfully!';
                            if (response.data.summary) {
                                const { successful = 0, failed = 0, skipped = 0 } = response.data.summary;
                                successMessage = `Successfully imported ${successful} customers. Failed: ${failed}, Skipped: ${skipped}`;
                            }

                            toast.current.show({
                                severity: 'success',
                                summary: 'Import Successful',
                                detail: successMessage,
                                life: 5000
                            });

                        }, 1000);

                    } else {
                        throw new Error(response.error || response.message);
                    }

                } catch (error) {
                    console.error('Import error:', error);
                    toast.current.show({
                        severity: 'error',
                        summary: 'Import Failed',
                        detail: error.message,
                        life: 7000
                    });
                    setUploading(false);
                    setImportCompleted(false);
                }
            };

            reader.onerror = () => {
                toast.current.show({
                    severity: 'error',
                    summary: 'File Read Error',
                    detail: 'Failed to read the CSV file',
                    life: 5000
                });
                setUploading(false);
                setImportCompleted(false);
            };

            reader.readAsText(selectedFile);

        } catch (error) {
            console.error('Import error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Import Failed',
                detail: error.message,
                life: 5000
            });
            setUploading(false);
            setImportCompleted(false);
        }
    };

    const handleCloseDialog = () => {
        setShowImportDialog(false);
        setSelectedFile(null);
        setUploading(false);
        setImportCompleted(false);
        setImportResults(null);
        setUploadProgress(0);
        setImportProgress({
            phase: '',
            message: '',
            total: 0
        });
        navigate('/facility/customer_management/customers');
    };

    const handleClear = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber('');
        setIdNumber('');
        setNextOfKinName('');
        setNextOfKinRelationship('');
        setNextOfKinPhoneNumber('');
        setNextOfKinEmail('');
        setCanReceiveInvoice(false);
        setCombineInvoices(false);
        setKraPin('');
        setUnits([]);
        setCustomerType('');
        setResidentType('');
        setNonResidentUsage('');
    };

    useEffect(() => {
        fetchUnits(customerType);
    }, [customerType]);

    useEffect(() => {
        if (customerKind === "Individual") {
            const generatedName = [
                salutation,
                firstName,
                lastName
            ]
                .filter(Boolean)
                .join(" ");

            setDisplayName(generatedName);
        }
    }, [
        customerKind,
        salutation,
        firstName,
        lastName
    ]);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/app/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/customer_management"}>Member Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/customer_management/add_new_customer"}>Add New Member</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="row">
                        <div className="col-sm-12">

                            <div className="card">
                                <div className="card-header">
                                    <Link to={"#"} onClick={() => navigate(-1)}><span><i className="ti ti-arrow-narrow-left"></i> Back</span></Link>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="container">

                                                <div className="card">
                                                    <div className="card-header d-flex justify-content-between align-items-center">
                                                        <h5 className="mb-0">Customer Info</h5>

                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => setShowImportDialog(true)}
                                                        >
                                                            <i className="bi bi-upload me-2"></i>
                                                            Import Customers
                                                        </button>
                                                    </div>



                                                    {/* <div className="card-body">
                                                        <div className='container'>
                                                            <div className='row'>
                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">First Name<span style={{ color: 'red' }}>*</span></label>
                                                                        <input
                                                                            ref={firstNameRef}
                                                                            type="text"
                                                                            className="mb-3 form-control"
                                                                            value={firstName}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setFirstName(value)
                                                                                setFormErrors((prev) => ({ ...prev, firstName: '' })); // clear error on change
                                                                            }}
                                                                        />
                                                                        {formErrors.firstName && <div className="text-danger small">{formErrors.firstName}</div>}
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">Last Name<span style={{ color: 'red' }}>*</span></label>
                                                                        <input
                                                                            ref={lastNameRef}
                                                                            type="text"
                                                                            className="mb-3 form-control"
                                                                            value={lastName}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setLastName(value)
                                                                                setFormErrors((prev) => ({ ...prev, firstName: '' })); // clear error on change
                                                                            }}
                                                                        />
                                                                        {formErrors.lastName && <div className="text-danger small">{formErrors.lastName}</div>}
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">Email<span style={{ color: 'red' }}>*</span></label>
                                                                        <input
                                                                            ref={emailRef}
                                                                            type="email"
                                                                            className="mb-3 form-control"
                                                                            value={email}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setEmail(value)
                                                                                setFormErrors((prev) => ({ ...prev, email: '' })); // clear error on change
                                                                            }}
                                                                        />
                                                                        {formErrors.email && <div className="text-danger small">{formErrors.email}</div>}
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">Mobile Number<span style={{ color: 'red' }}>*</span></label>
                                                                        <input
                                                                            ref={phoneNumberRef}
                                                                            type="text"
                                                                            className="mb-3 form-control"
                                                                            placeholder="e.g. +254 712 345 678"
                                                                            value={phoneNumber}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setPhoneNumber(value)
                                                                                setFormErrors((prev) => ({ ...prev, phoneNumber: '' })); // clear error on change
                                                                            }}
                                                                        />
                                                                        {formErrors.phoneNumber && <div className="text-danger small">{formErrors.phoneNumber}</div>}
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">ID Number / Passport Number<span style={{ color: 'red' }}>*</span></label>
                                                                        <input
                                                                            ref={idNumberRef}
                                                                            type="text"
                                                                            className="mb-3 form-control"
                                                                            value={idNumber}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setIdNumber(value)
                                                                                setFormErrors((prev) => ({ ...prev, idNumber: '' })); // clear error on change
                                                                            }}
                                                                        />
                                                                        {formErrors.idNumber && <div className="text-danger small">{formErrors.idNumber}</div>}
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">KRA PIN</label>
                                                                        <input
                                                                            ref={kraPinRef}
                                                                            type="text"
                                                                            className="mb-3 form-control"
                                                                            placeholder="e.g. A001234567Z"
                                                                            value={kraPin}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value;
                                                                                setKraPin(value)
                                                                                setFormErrors((prev) => ({ ...prev, kraPin: '' })); // clear error on change
                                                                            }}
                                                                        />
                                                                        {formErrors.kraPin && <div className="text-danger small">{formErrors.kraPin}</div>}
                                                                        <div className="text-muted small">Optional — required for eTims tax integration. Format: A001234567Z</div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label>Customer Type<span style={{ color: 'red' }}>*</span></label>
                                                                        <select
                                                                            ref={customerTypeRef}
                                                                            className="form-control mt-2"
                                                                            value={customerType}
                                                                            onChange={(e) => {
                                                                                const selectedType = e.target.value;
                                                                                setCustomerType(selectedType);
                                                                                setFormErrors((prev) => ({ ...prev, customerType: '' })); // clear error on change

                                                                                // If tenant is selected, set residentType to "resident"
                                                                                if (selectedType === "tenant") {
                                                                                    setResidentType("resident");
                                                                                } else {
                                                                                    // Reset residentType when changing away from tenant
                                                                                    setResidentType("");
                                                                                }
                                                                            }}
                                                                        >
                                                                            <option value="">Select</option>
                                                                            <option value="home owner">Home Owner</option>
                                                                            <option value="tenant">Tenant</option>
                                                                        </select>
                                                                        {formErrors.customerType && <div className="text-danger small">{formErrors.customerType}</div>}
                                                                    </div>
                                                                </div>

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            Resident Type<span style={{ color: 'red' }}>*</span>
                                                                        </label>

                                                                        <div ref={residentTypeRef}>
                                                                            <div className="form-check mt-2">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="residentType"
                                                                                    value="resident"
                                                                                    checked={residentType === "resident"}
                                                                                    onChange={(e) => {
                                                                                        setResidentType(e.target.value);
                                                                                        setFormErrors((prev) => ({ ...prev, residentType: '' }));
                                                                                    }}
                                                                                    disabled={customerType === "tenant"} // Disable if tenant is selected
                                                                                />
                                                                                <label className="form-check-label">Resident</label>
                                                                            </div>

                                                                            <div className="form-check mt-2">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="residentType"
                                                                                    value="non-resident"
                                                                                    checked={residentType === "non-resident"}
                                                                                    onChange={(e) => {
                                                                                        setResidentType(e.target.value);
                                                                                        setFormErrors((prev) => ({ ...prev, residentType: '' }));
                                                                                    }}
                                                                                    disabled={customerType === "tenant"} // Disable if tenant is selected
                                                                                />
                                                                                <label className="form-check-label">Non-Resident</label>
                                                                            </div>
                                                                        </div>

                                                                        {formErrors.residentType && (
                                                                            <div className="text-danger small">{formErrors.residentType}</div>
                                                                        )}
                                                                    </div>
                                                                </div>


                                                                {residentType === "non-resident" && (
                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <label>Please specify usage (e.g., Lease or Airbnb)</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={nonResidentUsage}
                                                                                onChange={(e) => setNonResidentUsage(e.target.value)}
                                                                                placeholder="Lease or Airbnb"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <div className="col-sm-6">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            Unit<span style={{ color: 'red' }}>*</span>
                                                                        </label>
                                                                        <div ref={unitsRef}>
                                                                            <Autocomplete
                                                                                multiple
                                                                                options={units}
                                                                                getOptionLabel={(option) => option.name}
                                                                                value={selectedUnits}
                                                                                onChange={(event, newValue) => {
                                                                                    setSelectedUnits(newValue);
                                                                                    setFormErrors((prev) => ({ ...prev, units: '' })); // clear error on change
                                                                                }}
                                                                                renderInput={(params) => (
                                                                                    <TextField {...params} label="Select Units" variant="outlined" />
                                                                                )}
                                                                            />
                                                                        </div>
                                                                        {formErrors.units && (
                                                                            <div className="text-danger small">{formErrors.units}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div> */}

                                                    <div className="card-body">
                                                        <div className="container">

                                                            {/* =====================================================
            CUSTOMER TYPE - INDIVIDUAL / BUSINESS
        ====================================================== */}
                                                            <div className="mb-4" ref={customerKindRef}>

                                                                <label className="form-label fw-semibold">
                                                                    Customer Type
                                                                    <span style={{ color: 'red' }}>*</span>
                                                                </label>

                                                                <div className="row g-3">

                                                                    {/* INDIVIDUAL */}
                                                                    <div className="col-md-6">
                                                                        <div
                                                                            className={`border rounded p-3 h-100 ${customerKind === "Individual"
                                                                                ? "border-primary bg-light"
                                                                                : ""
                                                                                }`}
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={() => {
                                                                                setCustomerKind("Individual");
                                                                                setFormErrors({});
                                                                            }}
                                                                        >
                                                                            <div className="form-check">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="customerKind"
                                                                                    value="Individual"
                                                                                    checked={customerKind === "Individual"}
                                                                                    onChange={() => {
                                                                                        setCustomerKind("Individual");
                                                                                        setFormErrors({});
                                                                                    }}
                                                                                />

                                                                                <label className="form-check-label fw-semibold">
                                                                                    Individual
                                                                                </label>
                                                                            </div>

                                                                            <div className="text-muted small ms-4 mt-1">
                                                                                Personal customer or resident
                                                                            </div>
                                                                        </div>
                                                                    </div>


                                                                    {/* BUSINESS */}
                                                                    <div className="col-md-6">
                                                                        <div
                                                                            className={`border rounded p-3 h-100 ${customerKind === "Business"
                                                                                ? "border-primary bg-light"
                                                                                : ""
                                                                                }`}
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={() => {
                                                                                setCustomerKind("Business");
                                                                                setFormErrors({});
                                                                            }}
                                                                        >
                                                                            <div className="form-check">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="customerKind"
                                                                                    value="Business"
                                                                                    checked={customerKind === "Business"}
                                                                                    onChange={() => {
                                                                                        setCustomerKind("Business");
                                                                                        setFormErrors({});
                                                                                    }}
                                                                                />

                                                                                <label className="form-check-label fw-semibold">
                                                                                    Business
                                                                                </label>
                                                                            </div>

                                                                            <div className="text-muted small ms-4 mt-1">
                                                                                Company or organisation
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                </div>

                                                                {formErrors.customerKind && (
                                                                    <div className="text-danger small mt-1">
                                                                        {formErrors.customerKind}
                                                                    </div>
                                                                )}
                                                            </div>


                                                            {/* =====================================================
            BUSINESS INFORMATION
        ====================================================== */}
                                                            {customerKind === "Business" && (
                                                                <div className="border rounded p-4 mb-4">

                                                                    <h6 className="fw-semibold mb-1">
                                                                        Business Information
                                                                    </h6>

                                                                    <p className="text-muted small mb-4">
                                                                        Enter the company details and the person who will
                                                                        be the primary contact.
                                                                    </p>

                                                                    <div className="row">

                                                                        {/* COMPANY NAME */}
                                                                        <div className="col-sm-6">
                                                                            <div className="mb-3">

                                                                                <label className="form-label">
                                                                                    Company Name
                                                                                    <span style={{ color: 'red' }}>*</span>
                                                                                </label>

                                                                                <input
                                                                                    ref={companyNameRef}
                                                                                    type="text"
                                                                                    className="form-control"
                                                                                    value={companyName}
                                                                                    placeholder="e.g. ABC Properties Limited"
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value;

                                                                                        setCompanyName(value);

                                                                                        // Business display name defaults
                                                                                        // to company name
                                                                                        setDisplayName(value);

                                                                                        setFormErrors((prev) => ({
                                                                                            ...prev,
                                                                                            companyName: '',
                                                                                            displayName: ''
                                                                                        }));
                                                                                    }}
                                                                                />

                                                                                {formErrors.companyName && (
                                                                                    <div className="text-danger small">
                                                                                        {formErrors.companyName}
                                                                                    </div>
                                                                                )}

                                                                            </div>
                                                                        </div>


                                                                        {/* DISPLAY NAME */}
                                                                        <div className="col-sm-6">
                                                                            <div className="mb-3">

                                                                                <label className="form-label">
                                                                                    Display Name
                                                                                    <span style={{ color: 'red' }}>*</span>
                                                                                </label>

                                                                                <input
                                                                                    ref={displayNameRef}
                                                                                    type="text"
                                                                                    className="form-control"
                                                                                    value={displayName}
                                                                                    placeholder="Name displayed on invoices"
                                                                                    onChange={(e) => {
                                                                                        setDisplayName(e.target.value);

                                                                                        setFormErrors((prev) => ({
                                                                                            ...prev,
                                                                                            displayName: ''
                                                                                        }));
                                                                                    }}
                                                                                />

                                                                                <div className="text-muted small mt-1">
                                                                                    Defaults to the company name and can be
                                                                                    changed if required.
                                                                                </div>

                                                                                {formErrors.displayName && (
                                                                                    <div className="text-danger small">
                                                                                        {formErrors.displayName}
                                                                                    </div>
                                                                                )}

                                                                            </div>
                                                                        </div>

                                                                    </div>
                                                                </div>
                                                            )}


                                                            {/* =====================================================
            PERSONAL / PRIMARY CONTACT INFORMATION
        ====================================================== */}
                                                            <div className="border rounded p-4 mb-4">

                                                                <h6 className="fw-semibold mb-1">
                                                                    {customerKind === "Business"
                                                                        ? "Primary Contact Person"
                                                                        : "Personal Information"}
                                                                </h6>

                                                                <p className="text-muted small mb-4">
                                                                    {customerKind === "Business"
                                                                        ? "Enter the details of the person responsible for this business."
                                                                        : "Enter the customer's personal details."}
                                                                </p>


                                                                <div className="row">

                                                                    {/* SALUTATION */}
                                                                    <div className="col-sm-3">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Salutation
                                                                            </label>

                                                                            <select
                                                                                className="form-control"
                                                                                value={salutation}
                                                                                onChange={(e) => {
                                                                                    setSalutation(e.target.value);
                                                                                }}
                                                                            >
                                                                                <option value="">Select</option>
                                                                                <option value="Mr">Mr</option>
                                                                                <option value="Mrs">Mrs</option>
                                                                                <option value="Ms">Ms</option>
                                                                                <option value="Miss">Miss</option>
                                                                                <option value="Dr">Dr</option>
                                                                                <option value="Prof">Prof</option>
                                                                                <option value="Rev">Rev</option>
                                                                            </select>

                                                                        </div>
                                                                    </div>


                                                                    {/* FIRST NAME */}
                                                                    <div className="col-sm-4">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                First Name
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <input
                                                                                ref={firstNameRef}
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={firstName}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;

                                                                                    setFirstName(value);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        firstName: ''
                                                                                    }));
                                                                                }}
                                                                            />

                                                                            {formErrors.firstName && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.firstName}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>


                                                                    {/* LAST NAME */}
                                                                    <div className="col-sm-5">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Last Name
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <input
                                                                                ref={lastNameRef}
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={lastName}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;

                                                                                    setLastName(value);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        lastName: ''
                                                                                    }));
                                                                                }}
                                                                            />

                                                                            {formErrors.lastName && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.lastName}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>


                                                                    {/* DISPLAY NAME - INDIVIDUAL */}
                                                                    {customerKind === "Individual" && (
                                                                        <div className="col-sm-6">
                                                                            <div className="mb-3">

                                                                                <label className="form-label">
                                                                                    Display Name
                                                                                    <span style={{ color: 'red' }}>*</span>
                                                                                </label>

                                                                                <input
                                                                                    ref={displayNameRef}
                                                                                    type="text"
                                                                                    className="form-control"
                                                                                    value={displayName}
                                                                                    placeholder="e.g. Dr John Doe"
                                                                                    onChange={(e) => {
                                                                                        setDisplayName(e.target.value);

                                                                                        setFormErrors((prev) => ({
                                                                                            ...prev,
                                                                                            displayName: ''
                                                                                        }));
                                                                                    }}
                                                                                />

                                                                                <div className="text-muted small mt-1">
                                                                                    This name will appear on invoices,
                                                                                    statements and customer records.
                                                                                </div>

                                                                                {formErrors.displayName && (
                                                                                    <div className="text-danger small">
                                                                                        {formErrors.displayName}
                                                                                    </div>
                                                                                )}

                                                                            </div>
                                                                        </div>
                                                                    )}


                                                                    {/* EMAIL */}
                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Email
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <input
                                                                                ref={emailRef}
                                                                                type="email"
                                                                                className="form-control"
                                                                                value={email}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;

                                                                                    setEmail(value);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        email: ''
                                                                                    }));
                                                                                }}
                                                                            />

                                                                            {formErrors.email && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.email}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>


                                                                    {/* PHONE */}
                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Mobile Number
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <input
                                                                                ref={phoneNumberRef}
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="e.g. +254 712 345 678"
                                                                                value={phoneNumber}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;

                                                                                    setPhoneNumber(value);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        phoneNumber: ''
                                                                                    }));
                                                                                }}
                                                                            />

                                                                            {formErrors.phoneNumber && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.phoneNumber}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>


                                                                    {/* ID NUMBER */}
                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                ID Number / Passport Number
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <input
                                                                                ref={idNumberRef}
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={idNumber}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;

                                                                                    setIdNumber(value);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        idNumber: ''
                                                                                    }));
                                                                                }}
                                                                            />

                                                                            {formErrors.idNumber && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.idNumber}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </div>


                                                            {/* =====================================================
            KRA PIN
        ====================================================== */}
                                                            <div className="border rounded p-4 mb-4">

                                                                <h6 className="fw-semibold mb-1">
                                                                    Tax Information
                                                                </h6>

                                                                <p className="text-muted small mb-3">
                                                                    Optional tax information for invoicing and eTims integration.
                                                                </p>

                                                                <div className="row">

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                KRA PIN
                                                                            </label>

                                                                            <input
                                                                                ref={kraPinRef}
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="e.g. A001234567Z"
                                                                                value={kraPin}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;

                                                                                    setKraPin(value);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        kraPin: ''
                                                                                    }));
                                                                                }}
                                                                            />

                                                                            {formErrors.kraPin && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.kraPin}
                                                                                </div>
                                                                            )}

                                                                            <div className="text-muted small mt-1">
                                                                                Optional — required for eTims tax integration.
                                                                                Format: A001234567Z
                                                                            </div>

                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </div>


                                                            {/* =====================================================
            CUSTOMER / RESIDENT INFORMATION
        ====================================================== */}
                                                            <div className="border rounded p-4 mb-4">

                                                                <h6 className="fw-semibold mb-3">
                                                                    Customer & Residency Details
                                                                </h6>

                                                                <div className="row">

                                                                    {/* EXISTING CUSTOMER TYPE */}
                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Customer Category
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <select
                                                                                ref={customerTypeRef}
                                                                                className="form-control"
                                                                                value={customerType}
                                                                                onChange={(e) => {

                                                                                    const selectedType = e.target.value;

                                                                                    setCustomerType(selectedType);

                                                                                    setFormErrors((prev) => ({
                                                                                        ...prev,
                                                                                        customerType: ''
                                                                                    }));

                                                                                    if (selectedType === "tenant") {
                                                                                        setResidentType("resident");
                                                                                    } else {
                                                                                        setResidentType("");
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <option value="">
                                                                                    Select
                                                                                </option>

                                                                                <option value="home owner">
                                                                                    Home Owner
                                                                                </option>

                                                                                <option value="tenant">
                                                                                    Tenant
                                                                                </option>
                                                                            </select>

                                                                            {formErrors.customerType && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.customerType}
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </div>


                                                                    {/* RESIDENT TYPE */}
                                                                    <div className="col-sm-6">

                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Resident Type
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <div ref={residentTypeRef}>

                                                                                <div className="form-check mt-2">

                                                                                    <input
                                                                                        className="form-check-input"
                                                                                        type="radio"
                                                                                        name="residentType"
                                                                                        value="resident"
                                                                                        checked={
                                                                                            residentType === "resident"
                                                                                        }
                                                                                        onChange={(e) => {
                                                                                            setResidentType(
                                                                                                e.target.value
                                                                                            );

                                                                                            setFormErrors((prev) => ({
                                                                                                ...prev,
                                                                                                residentType: ''
                                                                                            }));
                                                                                        }}
                                                                                        disabled={
                                                                                            customerType === "tenant"
                                                                                        }
                                                                                    />

                                                                                    <label className="form-check-label">
                                                                                        Resident
                                                                                    </label>

                                                                                </div>


                                                                                <div className="form-check mt-2">

                                                                                    <input
                                                                                        className="form-check-input"
                                                                                        type="radio"
                                                                                        name="residentType"
                                                                                        value="non-resident"
                                                                                        checked={
                                                                                            residentType === "non-resident"
                                                                                        }
                                                                                        onChange={(e) => {
                                                                                            setResidentType(
                                                                                                e.target.value
                                                                                            );

                                                                                            setFormErrors((prev) => ({
                                                                                                ...prev,
                                                                                                residentType: ''
                                                                                            }));
                                                                                        }}
                                                                                        disabled={
                                                                                            customerType === "tenant"
                                                                                        }
                                                                                    />

                                                                                    <label className="form-check-label">
                                                                                        Non-Resident
                                                                                    </label>

                                                                                </div>

                                                                            </div>

                                                                            {formErrors.residentType && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.residentType}
                                                                                </div>
                                                                            )}

                                                                        </div>

                                                                    </div>


                                                                    {/* NON-RESIDENT USAGE */}
                                                                    {residentType === "non-resident" && (
                                                                        <div className="col-sm-6">

                                                                            <div className="mb-3">

                                                                                <label className="form-label">
                                                                                    Please specify usage
                                                                                </label>

                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control"
                                                                                    value={nonResidentUsage}
                                                                                    onChange={(e) =>
                                                                                        setNonResidentUsage(
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                    placeholder="e.g. Lease or Airbnb"
                                                                                />

                                                                            </div>

                                                                        </div>
                                                                    )}

                                                                </div>
                                                            </div>


                                                            {/* =====================================================
            UNITS
        ====================================================== */}
                                                            <div className="border rounded p-4 mb-4">

                                                                <h6 className="fw-semibold mb-3">
                                                                    Unit Assignment
                                                                </h6>

                                                                <div className="row">

                                                                    <div className="col-sm-6">

                                                                        <div className="mb-3">

                                                                            <label className="form-label">
                                                                                Unit
                                                                                <span style={{ color: 'red' }}>*</span>
                                                                            </label>

                                                                            <div ref={unitsRef}>

                                                                                <Autocomplete
                                                                                    multiple
                                                                                    options={units}
                                                                                    getOptionLabel={(option) =>
                                                                                        option.name
                                                                                    }
                                                                                    value={selectedUnits}
                                                                                    onChange={(event, newValue) => {

                                                                                        setSelectedUnits(newValue);

                                                                                        setFormErrors((prev) => ({
                                                                                            ...prev,
                                                                                            units: ''
                                                                                        }));
                                                                                    }}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            label="Select Units"
                                                                                            variant="outlined"
                                                                                        />
                                                                                    )}
                                                                                />

                                                                            </div>

                                                                            {formErrors.units && (
                                                                                <div className="text-danger small">
                                                                                    {formErrors.units}
                                                                                </div>
                                                                            )}

                                                                        </div>

                                                                    </div>

                                                                </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                </div>

                                                <div className='col-sm-12'>
                                                    <div className='card'>
                                                        <div className="card-header">
                                                            <h5>Next of Kin</h5>
                                                        </div>
                                                        <div className='card-body'>
                                                            <div className='container'>
                                                                <div className='row'>

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Full Name</label>
                                                                            <input
                                                                                type="text"
                                                                                className="mb-3 form-control"
                                                                                value={nextOfKinName}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;
                                                                                    setNextOfKinName(value)
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Relationship</label>
                                                                            <input
                                                                                type="text"
                                                                                className="mb-3 form-control"
                                                                                value={nextOfKinRelationship}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;
                                                                                    setNextOfKinRelationship(value)
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Phone Number</label>
                                                                            <input
                                                                                type="text"
                                                                                className="mb-3 form-control"
                                                                                value={nextOfKinPhoneNumber}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;
                                                                                    setNextOfKinPhoneNumber(value)
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <label className="form-label">Email</label>
                                                                            <input
                                                                                type="text"
                                                                                className="mb-3 form-control"
                                                                                value={nextOfKinEmail}
                                                                                onChange={(e) => {
                                                                                    const value = e.target.value;
                                                                                    setNextOfKinEmail(value)
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                checked={canReceiveInvoice}
                                                                                onChange={(e) => setCanReceiveInvoice(e.target.checked)}
                                                                            />
                                                                            <label className="form-label ms-2">
                                                                                Can Receive Invoice
                                                                            </label>
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-sm-6">
                                                                        <div className="mb-3">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                checked={combineInvoices}
                                                                                onChange={(e) => setCombineInvoices(e.target.checked)}
                                                                            />
                                                                            <label className="form-label ms-2">
                                                                                Combine Invoices
                                                                            </label>
                                                                            <div className="text-muted small">
                                                                                When checked, all invoices will be combined into a single invoice per billing period
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="text-end">
                                                    <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>Clear</button>
                                                    &nbsp;
                                                    <button type="button" className="btn btn-primary" onClick={HandleCustomerSubmit}>Submit</button>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                header={importCompleted ? "Import Completed" : "Import Customers"}
                visible={showImportDialog}
                style={{ width: '50vw' }}
                modal
                onHide={handleCloseDialog}
                closable={!uploading || importCompleted}
                footer={
                    <div>
                        {!importCompleted ? (
                            <>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        if (uploading) {
                                            if (window.confirm('Import is in progress. Are you sure you want to cancel?')) {
                                                handleCloseDialog();
                                            }
                                        } else {
                                            handleCloseDialog();
                                        }
                                    }}
                                    disabled={uploading}
                                >
                                    {uploading ? 'Cancel' : 'Close'}
                                </button>
                                <button
                                    className="btn btn-primary ms-2"
                                    onClick={handleImportSubmit}
                                    disabled={uploading || !selectedFile}
                                >
                                    {uploading ? (
                                        <>
                                            <i className="pi pi-spin pi-spinner me-2"></i>
                                            Importing...
                                        </>
                                    ) : (
                                        'Import Customers'
                                    )}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleCloseDialog}
                                >
                                    <i className="pi pi-check me-2"></i>
                                    Close
                                </button>
                            </>
                        )}
                    </div>
                }
            >
                <div className="p-fluid">
                    {/* Download CSV Template */}
                    <div className="mb-3">
                        <p>You can download a CSV template to prefill the required information:</p>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={downloadCSVTemplate}
                            disabled={uploading}
                        >
                            <i className="pi pi-download me-2"></i>
                            Download CSV Template
                        </button>
                    </div>

                    {/* Upload CSV */}
                    <div className="mb-3">
                        <p>Upload your CSV file:</p>
                        <div className="border rounded p-3">
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileSelect}
                                disabled={uploading}
                                className="form-control"
                            />
                            {selectedFile && (
                                <div className="mt-2">
                                    <small className="text-success">
                                        <i className="pi pi-check-circle me-1"></i>
                                        Selected: {selectedFile.name}
                                    </small>
                                </div>
                            )}
                        </div>

                        {/* Progress Section */}
                        {uploading && (
                            <div className="mt-4">
                                {/* Progress Bar */}
                                <div className="mb-3">
                                    <div className="progress" style={{ height: '25px' }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated"
                                            role="progressbar"
                                            style={{ width: `${uploadProgress}%` }}
                                            aria-valuenow={uploadProgress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        >
                                            {uploadProgress}%
                                        </div>
                                    </div>
                                </div>

                                {/* Status Message */}
                                <div className="alert alert-info mb-3">
                                    <div className="d-flex align-items-center">
                                        {importProgress.phase === 'completing' ? (
                                            <i className="pi pi-check-circle me-3" style={{ fontSize: '1.5rem', color: 'green' }}></i>
                                        ) : (
                                            <i className="pi pi-spin pi-spinner me-3" style={{ fontSize: '1.5rem' }}></i>
                                        )}
                                        <div>
                                            <strong>
                                                {importProgress.phase === 'validating' && '📋 Validating Data'}
                                                {importProgress.phase === 'uploading' && '📤 Uploading Data'}
                                                {importProgress.phase === 'processing' && '⚙️ Processing Customers'}
                                                {importProgress.phase === 'completing' && '✅ Import Completed!'}
                                            </strong>
                                            <div className="mt-1">
                                                <small>{importProgress.message}</small>
                                            </div>
                                            {importProgress.total > 0 && (
                                                <div className="mt-1">
                                                    <small className="text-muted">
                                                        {importProgress.phase === 'completing'
                                                            ? 'All customers have been imported successfully!'
                                                            : `Processing ${importProgress.total} customers...`}
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Show completion message */}
                                {importProgress.phase === 'completing' && (
                                    <div className="alert alert-success">
                                        <div className="d-flex align-items-start">
                                            <i className="pi pi-check-circle me-2 mt-1" style={{ color: 'green' }}></i>
                                            <div>
                                                <strong>Import Completed Successfully!</strong>
                                                <p className="mb-0 mt-1">
                                                    All customers have been imported. The dialog will close automatically in a few seconds...
                                                </p>
                                                {importResults && (
                                                    <div className="mt-2">
                                                        <small>
                                                            <strong>Summary:</strong> {importResults.summary.successful} successful,
                                                            {importResults.summary.failed} failed,
                                                            {importResults.summary.skipped} skipped
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Info - only show when not completed */}
                                {importProgress.phase !== 'completing' && (
                                    <div className="alert alert-warning">
                                        <div className="d-flex align-items-start">
                                            <i className="pi pi-info-circle me-2 mt-1"></i>
                                            <div>
                                                <strong>Please wait...</strong>
                                                <p className="mb-0 mt-1">
                                                    The system is creating customer accounts, assigning units, and setting up handovers.
                                                    This process may take several minutes for large imports.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Do Not Close Warning - only show when not completed */}
                                {importProgress.phase !== 'completing' && (
                                    <div className="text-center mt-2">
                                        <small className="text-danger">
                                            <i className="pi pi-exclamation-triangle me-1"></i>
                                            Please do not close this window during the import process
                                        </small>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Success Summary (After completion, before dialog closes) */}
                        {importResults && !uploading && (
                            <div className="mt-3 alert alert-success">
                                <h6>
                                    <i className="pi pi-check-circle me-2"></i>
                                    Import Summary
                                </h6>
                                <div className="row mt-3">
                                    <div className="col-4 text-center">
                                        <div className="text-success" style={{ fontSize: '2rem' }}>
                                            {importResults.summary.successful}
                                        </div>
                                        <small>Successful</small>
                                    </div>
                                    <div className="col-4 text-center">
                                        <div className="text-danger" style={{ fontSize: '2rem' }}>
                                            {importResults.summary.failed}
                                        </div>
                                        <small>Failed</small>
                                    </div>
                                    <div className="col-4 text-center">
                                        <div className="text-warning" style={{ fontSize: '2rem' }}>
                                            {importResults.summary.skipped}
                                        </div>
                                        <small>Skipped</small>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Instructions */}
                    {!uploading && (
                        <div className="alert alert-info">
                            <h6><i className="pi pi-info-circle me-2"></i>Instructions:</h6>
                            <ul className="mb-0">
                                <li>Download the template and fill in customer data</li>
                                <li><strong>Required:</strong> firstName, lastName, email, phoneNumber, idNumber, customerType, residentType, units</li>
                                <li><strong>Optional:</strong> kraPin (must match format A001234567Z if provided)</li>
                                <li>Maximum file size: 5MB</li>
                                <li>Large imports may take several minutes to complete</li>
                            </ul>
                        </div>
                    )}
                </div>
            </Dialog>
        </Layout>
    )
}

export default AddCustomer